export type GrowthPathStepState = 'done' | 'active' | 'upcoming';

export type GrowthPathIndicatorStatus = 'ok' | 'improve' | 'todo';

export type GrowthPathIndicator = {
  key?: string;
  label: string;
  value: string | number | null;
  unit?: string;
  hint?: string;
  status?: GrowthPathIndicatorStatus;
  action_url?: string;
};

export type GrowthPathStep = {
  state: GrowthPathStepState;
  title: string;
  key?: string;
  group?: string;
  action_url?: string;
  action_label?: string;
  checklist?: string[];
  indicators: GrowthPathIndicator[];
};

export type GrowthPath = {
  completed_count: number;
  total_count: number;
  steps: GrowthPathStep[];
};

const asRecord = (raw: unknown): Record<string, unknown> | null =>
  raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;

const trimString = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string') return undefined;
  const value = raw.trim();
  return value || undefined;
};

const normalizeStepState = (raw: unknown): GrowthPathStepState => {
  const value = String(raw ?? '').toLowerCase();
  if (value === 'done' || value === 'completed') return 'done';
  if (value === 'active' || value === 'current') return 'active';
  return 'upcoming';
};

const primitiveValue = (raw: unknown): string | number | null => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') return raw.trim() || null;
  if (typeof raw === 'boolean') return raw ? 'بله' : 'خیر';
  return null;
};

const STATUS_LABEL: Record<GrowthPathIndicatorStatus, string> = {
  ok: 'مناسب',
  improve: 'قابل بهبود',
  todo: 'انجام نشده',
};

const normalizeStatus = (raw: unknown): GrowthPathIndicatorStatus | undefined => {
  const value = String(raw ?? '').toLowerCase();
  if (value === 'ok' || value === 'improve' || value === 'todo') return value;
  return undefined;
};

const bandLabel = (raw: unknown): { label?: string; unit?: string } => {
  const band = asRecord(raw);
  if (!band) return {};
  const label = trimString(band.label);
  const unit = trimString(band.unit);
  return { ...(label ? { label } : {}), ...(unit ? { unit } : {}) };
};

export const normalizeGrowthIndicator = (raw: unknown, fallbackKey?: string): GrowthPathIndicator | null => {
  if (typeof raw === 'string') {
    const label = raw.trim();
    return label ? { key: fallbackKey, label, value: null } : null;
  }

  const item = asRecord(raw);
  if (!item) {
    if (raw == null || fallbackKey == null) return null;
    const value = primitiveValue(raw);
    if (value == null) return null;
    return { key: fallbackKey, label: fallbackKey, value };
  }

  const key = trimString(item.key) ?? fallbackKey;
  const band = bandLabel(item.band);
  const label =
    trimString(item.label) ??
    trimString(item.title) ??
    trimString(item.name) ??
    band.label ??
    key ??
    '';
  if (!label) return null;

  const status = normalizeStatus(item.status);
  const value =
    primitiveValue(item.value ?? item.current ?? item.count ?? item.amount) ?? band.label ?? null;

  const unit = trimString(item.unit) ?? band.unit;
  const hint = trimString(item.hint) ?? trimString(item.description) ?? trimString(item.subtitle);
  const actionUrl = trimString(item.action_url) ?? trimString(item.embed_url);

  return {
    key,
    label,
    value,
    ...(unit ? { unit } : {}),
    ...(hint ? { hint } : {}),
    ...(status ? { status } : {}),
    ...(actionUrl ? { action_url: actionUrl } : {}),
  };
};

export const normalizeGrowthIndicatorsCatalog = (raw: unknown): GrowthPathIndicator[] => {
  if (Array.isArray(raw)) {
    return raw
      .map(item => normalizeGrowthIndicator(item))
      .filter((item): item is GrowthPathIndicator => item != null);
  }
  return [];
};

export const normalizeGrowthIndicatorsList = (raw: unknown): GrowthPathIndicator[] => {
  const fromArray = normalizeGrowthIndicatorsCatalog(raw);
  if (fromArray.length) return fromArray;

  const map = asRecord(raw);
  if (!map) return [];

  return Object.entries(map)
    .map(([key, value]) => normalizeGrowthIndicator(value, key))
    .filter((item): item is GrowthPathIndicator => item != null);
};

export const normalizeGrowthIndicatorsByKey = (raw: unknown): Record<string, GrowthPathIndicator[]> => {
  const map = asRecord(raw);
  if (!map) return {};

  const next: Record<string, GrowthPathIndicator[]> = {};
  for (const [key, value] of Object.entries(map)) {
    const list = normalizeGrowthIndicatorsList(value);
    if (list.length) next[key] = list;
  }
  return next;
};

const pickIndicatorsForStep = (
  step: Record<string, unknown>,
  stepKey: string | undefined,
  byKey: Record<string, GrowthPathIndicator[]>,
  catalog: GrowthPathIndicator[],
): GrowthPathIndicator[] => {
  const fromStep = normalizeGrowthIndicatorsList(step.indicators);
  if (fromStep.length) return fromStep;
  if (stepKey) {
    const fromCatalog = catalog.filter(item => item.key === stepKey);
    if (fromCatalog.length) return fromCatalog;
    if (byKey[stepKey]) return byKey[stepKey];
  }
  return [];
};

const normalizeChecklist = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
};

export const normalizeGrowthPath = (raw: unknown, extras?: unknown): GrowthPath | null => {
  if (!raw || typeof raw !== 'object') return null;
  const path = raw as Record<string, unknown>;
  const extra = asRecord(extras) ?? {};
  const catalog = [
    ...normalizeGrowthIndicatorsCatalog(extra.indicators),
    ...normalizeGrowthIndicatorsCatalog(path.indicators),
  ];
  const byKey = {
    ...normalizeGrowthIndicatorsByKey(extra.indicators),
    ...normalizeGrowthIndicatorsByKey(path.indicators),
  };
  const stepsRaw = path.steps;
  if (!Array.isArray(stepsRaw) || stepsRaw.length === 0) return null;

  const steps: GrowthPathStep[] = [];
  for (const item of stepsRaw) {
    if (!item || typeof item !== 'object') continue;
    const step = item as Record<string, unknown>;
    const title = typeof step.title === 'string' ? step.title.trim() : '';
    if (!title) continue;
    const key = trimString(step.key);
    const group = trimString(step.group);
    const action =
      asRecord(step.action) ??
      (typeof step.action === 'string' ? { url: step.action } : null);
    const indicators = pickIndicatorsForStep(step, key, byKey, catalog);
    const actionUrl =
      trimString(step.action_url) ??
      trimString(action?.url) ??
      trimString(action?.href) ??
      indicators.find(indicator => indicator.action_url)?.action_url;
    const actionLabel =
      trimString(step.action_label) ?? trimString(action?.label) ?? trimString(action?.title);
    const checklist = normalizeChecklist(step.checklist);
    const normalized: GrowthPathStep = {
      state: normalizeStepState(step.state),
      title,
      indicators,
    };
    if (key) normalized.key = key;
    if (group) normalized.group = group;
    if (actionUrl) normalized.action_url = actionUrl;
    if (actionLabel) normalized.action_label = actionLabel;
    if (checklist.length) normalized.checklist = checklist;
    steps.push(normalized);
  }

  if (steps.length === 0) return null;

  const completed =
    typeof path.completed_count === 'number'
      ? path.completed_count
      : steps.filter(step => step.state === 'done').length;
  const total =
    typeof path.total_count === 'number' ? path.total_count : Math.max(completed, steps.length);

  return { completed_count: completed, total_count: total, steps };
};

export const formatGrowthIndicatorValue = (indicator: GrowthPathIndicator): string => {
  if (indicator.value == null) return '—';
  const formatted =
    typeof indicator.value === 'number' ? indicator.value.toLocaleString('fa-IR') : indicator.value;
  return indicator.unit ? `${formatted} ${indicator.unit}` : formatted;
};

export const growthIndicatorStatusLabel = (status?: GrowthPathIndicatorStatus) =>
  status ? STATUS_LABEL[status] : undefined;

export const getGrowthPathTeaser = (path: GrowthPath) => {
  const remaining = Math.max(0, path.total_count - path.completed_count);
  const percent = path.total_count > 0 ? Math.min(100, Math.round((path.completed_count / path.total_count) * 100)) : 0;
  const stepTitle =
    path.steps.find(step => step.state === 'active')?.title ??
    path.steps.find(step => step.state === 'upcoming')?.title ??
    null;

  return {
    remaining,
    percent,
    stepTitle,
    complete: path.total_count > 0 && remaining === 0,
  };
};
